import { motion } from 'framer-motion';
import { Marquee } from '../Marquee';
import { useLanguage } from '../LanguageProvider';

import testimonialData from '../../data/testimonials.json';

const reviews = testimonialData;

const firstRow = [reviews[0], reviews[1], reviews[2]];
const secondRow = [reviews[3], reviews[4], reviews[5]];
const thirdRow = [reviews[1], reviews[5], reviews[0]];

const gamerReviews = reviews.map((r, i) => {
    const gamerTexts = [
        "Got my Steam key instantly! Best prices I've seen so far. Highly recommend Gameswap.",
        "Super fast and transparent. I saved a lot on the latest AAA titles.",
        "No waiting time at all. Bought a game bundle and redeemed it immediately.",
        "Legit keys and instant delivery. What more could you ask for?",
        "Customer support was super helpful. A+ gaming marketplace experience.",
        "I love the clean interface and the massive discounts on Epic keys."
    ];
    return {
        ...r,
        name: ["Alex R.", "Sarah M.", "Jordan K.", "Chris T.", "Mia L.", "David B."][i % 6],
        username: ["@alexgaming", "@sarah_plays", "@jordan_k", "@chris_t", "@mia_lvl99", "@dave_b"][i % 6],
        body: { en: gamerTexts[i % 6] }
    };
});

const gamerFirstRow = [gamerReviews[0], gamerReviews[1], gamerReviews[2]];
const gamerSecondRow = [gamerReviews[3], gamerReviews[4], gamerReviews[5]];
const gamerThirdRow = [gamerReviews[1], gamerReviews[5], gamerReviews[0]];

function bodyFor(body, language) {
    if (body && typeof body === 'object') return body[language] ?? body.en;
    return body;
}

const ReviewCard = ({ img, name, username, body, language }) => {
    return (
        <figure
            className="relative h-full w-64 cursor-pointer overflow-hidden rounded-xl border p-4 border-gray-950/[.1] bg-gray-950/[.01] hover:bg-gray-950/[.05] dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15] transition-colors"
        >
            <div className="flex flex-row items-center gap-2">
                <img className="rounded-full" width="32" height="32" alt="" src={img} />
                <div className="flex flex-col">
                    <figcaption className="text-sm font-medium dark:text-white">
                        {name}
                    </figcaption>
                    <p className="text-xs font-medium dark:text-white/40">{username}</p>
                </div>
            </div>
            <blockquote className="mt-2 text-sm text-gray-800 dark:text-gray-300">
                &ldquo;{bodyFor(body, language)}&rdquo;
            </blockquote>
        </figure>
    );
};

export default function TestimonialsSection({ isGameswap }) {
    const { language, t } = useLanguage();
    const primaryColor = isGameswap ? '#ec4899' : '#FF9500';
    const displayFirstRow = isGameswap ? gamerFirstRow : firstRow;
    const displaySecondRow = isGameswap ? gamerSecondRow : secondRow;
    const displayThirdRow = isGameswap ? gamerThirdRow : thirdRow;

    return (
        <section id="testimonials" className={`py-20 relative overflow-hidden ${isGameswap ? 'bg-transparent z-10' : 'bg-white dark:bg-[#0a0a0a]'}`}>
            <div className="mx-auto max-w-7xl px-4 relative z-10">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <motion.span
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.5 }}
                        className="inline-block text-xs font-medium uppercase tracking-widest mb-3"
                        style={{ color: primaryColor }}
                    >
                        {isGameswap ? 'COMMUNITY' : t('testimonials.eyebrow')}
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.5 }}
                        className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white"
                    >
                        {isGameswap ? 'What gamers say' : t('testimonials.title')}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ delay: 0.1, duration: 0.5 }}
                        className="mt-3 text-base text-gray-600 dark:text-[#888888]"
                    >
                        {isGameswap ? 'Real feedback from our gaming community.' : t('testimonials.sub')}
                    </motion.p>
                </div>
            </div>

            <div className="relative flex w-full flex-col items-center justify-center overflow-hidden">
                    {/* Row 1 — scrolls LEFT */}
                    <Marquee pauseOnHover className="[--duration:25s]">
                        {displayFirstRow.map((review, i) => (
                            <ReviewCard key={i} {...review} language={language} />
                        ))}
                    </Marquee>
                    {/* Row 2 — scrolls RIGHT */}
                    <Marquee reverse pauseOnHover className="[--duration:25s]">
                        {displaySecondRow.map((review, i) => (
                            <ReviewCard key={i} {...review} language={language} />
                        ))}
                    </Marquee>
                    {/* Row 3 — scrolls LEFT */}
                    <Marquee pauseOnHover className="[--duration:25s]">
                        {displayThirdRow.map((review, i) => (
                            <ReviewCard key={i} {...review} language={language} />
                        ))}
                    </Marquee>
                    {/* Gradient edges */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-white dark:from-[#0a0a0a] to-transparent z-10"></div>
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-white dark:from-[#0a0a0a] to-transparent z-10"></div>
                </div>
        </section>
    );
}
